import React, {useEffect, useMemo, useRef, useState} from 'react';
import {Dimensions, Image, Pressable, View} from 'react-native';
import MaterialCommunityIcons from '@expo/vector-icons/MaterialCommunityIcons';
import Carousel from "react-native-reanimated-carousel";

const WIDTH = Dimensions.get('window').width * 0.165;
const HEIGHT = Dimensions.get('window').height * 0.30;

const CardImageContainer = ({isSessionView=false, images=null}) => {
    const [activeDot, setActiveDot] = useState(0);
    const [isHovered, setIsHovered] = useState(false);
    const carouselRef = useRef<any>(null);

    const styles = {
        sessionCard: {
            width: WIDTH,
            height: isSessionView ? HEIGHT * 0.75 : HEIGHT,
        },
        image: {
            width: WIDTH,
            height: isSessionView ? HEIGHT * 0.75 : HEIGHT,
            borderRadius: 30,
            overflow: 'hidden',
        },
        dot: {
            width: 8,
            height: 8,
            borderRadius: 50,
            backgroundColor: 'white',
            marginHorizontal: 5,
        },
        dotContainer: {
            position: 'absolute',
            flexDirection: 'row',
            justifyContent: 'center',
            alignItems: 'center',
            bottom: '5%',
            left: '43%',
        },
        arrow: {
            backgroundColor: 'white',
            width: 30,
            height: 30,
            borderRadius: 15,
            position: 'absolute',
            top: '45%'
        },
        arrowLeft: {
            left: '5%',
        },
        arrowRight: {
            right: '5%',
        },
        arrowContainer: {
            flexDirection: 'row',
            zIndex: 10,
        },
    };

    // Use provided images or fallback to default images
    const defaultImages = [
        require('../../../../assets/images/camp-image3.jpeg'),
        require('../../../../assets/images/camp-image2.jpeg'),
        require('../../../../assets/images/camp-image1.jpg'),
    ];
    
    const normalizedImages = useMemo(() => {
        if (!Array.isArray(images)) return [];
        return images
            .map((img) => {
                if (!img) return null;
                if (typeof img === 'string') {
                    const uri = img.trim();
                    return uri ? { uri } : null;
                }
                if (typeof img === 'object' && typeof img.uri === 'string') {
                    const uri = img.uri.trim();
                    return uri ? { uri } : null;
                }
                return null;
            })
            .filter(Boolean) as Array<{ uri: string }>;
    }, [images]);

    const campImages = normalizedImages.length > 0 ? normalizedImages : defaultImages;

    useEffect(() => {
        if (activeDot >= campImages.length) {
            setActiveDot(0);
        }
    }, [activeDot, campImages.length]);

    const goToPrevImage = () => {
        if (campImages.length <= 1) return;
        const nextIndex = activeDot === 0 ? campImages.length - 1 : activeDot - 1;
        setActiveDot(nextIndex);
        carouselRef.current?.scrollTo?.({ index: nextIndex, animated: true });
    };

    const goToNextImage = () => {
        if (campImages.length <= 1) return;
        const nextIndex = activeDot === campImages.length - 1 ? 0 : activeDot + 1;
        setActiveDot(nextIndex);
        carouselRef.current?.scrollTo?.({ index: nextIndex, animated: true });
    };

    return (
        <View style={styles.sessionCard}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
        >
            <Carousel
                ref={carouselRef}
                width={WIDTH}
                height={HEIGHT}
                data={campImages}
                scrollAnimationDuration={300}
                onSnapToItem={(index) => setActiveDot(index)}
                renderItem={
                    ({item}) => (
                        <Image
                            source={item}
                            resizeMode="cover"
                            style={styles.image}
                        />
                    )
                }
            />
            <View style={styles.dotContainer}>
                {campImages.map((_, index) => (
                    <View
                        key={index}
                        style={
                            [styles.dot, {backgroundColor: index === activeDot ? '#FFFFFF' : 'rgba(255, 255, 255, 0.5)'}]
                        }
                    />
                ))}
            </View>
            {
                isHovered &&
                <>
                    <Pressable onPress={goToPrevImage} style={[styles.arrow, styles.arrowLeft]}>
                        <MaterialCommunityIcons name="chevron-left" size={30} color="black" />
                    </Pressable>

                    <Pressable onPress={goToNextImage} style={[styles.arrow, styles.arrowRight]}>
                        <MaterialCommunityIcons name="chevron-right" size={30} color="black" />
                    </Pressable>
                </>
            }
        </View>
    );
};

export default CardImageContainer;